'use client';

/**
 * @fileoverview `DiscoverPickSet` — the DS 1.5.0+ "Picked for you" three-up
 * pick set rendered atop the Discover list view (WEB-677 / DPL-27),
 * superseding the single-event `DiscoverPickedForYouHero` (FRE-915).
 *
 * Pure presentational molecule. Renders 1–3 ranked pick cards under a header
 * (eyebrow + right-aligned caption) — never padded to fill the grid, and
 * never rendered at all when the caller passes zero picks (though the gated
 * builder in `lib/discover/pickSet.ts` already returns `null` in that case,
 * so this component's own callers should not mount it with an empty array).
 *
 * Grid `repeat(auto-fit, minmax(272px, 1fr))`: three across at 1440, two mid,
 * one narrow.
 *
 * Each card: a 56px media slot (real poster via `next/image`, or an explicit
 * "Poster pending" hatched placeholder — never a gradient fill) with a 3px
 * category-colored left edge, a 2-line title, an optional 2-line why-line
 * (honest-absence — omitted when neither a curated reason nor a server
 * `whyReasons` entry backs one) — the card's only taste element (FRE-1418:
 * taste is reasons, never a score or ring) — and a compact meta
 * row (category signature · time · venue · price) that wraps to a second
 * line (WEB-789 / DPL-27) instead of ellipsising each item.
 *
 * **WEB-678 / DPL-27 (≤720 snap rail).** Below 721px the grid becomes a
 * horizontal `scroll-snap-type: x mandatory` rail: cards go `flex: 0 0 82%`
 * (`min-w-[248px] max-w-[320px]`) so the next pick always peeks, min-height
 * is 108px (44px media, 1-line title/why-line; wrap may grow), and the rail
 * starts flush at
 * the container's own left edge and bleeds right only —
 * **no negative `margin-left`**, `scroll-padding-left: 0`, `scroll-snap-align:
 * start` on every card so the resting scroll position always lands a card
 * flush at the gutter, never mid-card. The scrollbar is hidden (`scrollbar-
 * width: none` + the WebKit pseudo-element), but the container stays a plain
 * `overflow-x-auto` region — never `overflow: hidden` — so it remains
 * trackpad/touch-scrollable AND keyboard-reachable: each card is an ordinary
 * in-DOM `<a>`, so Tab reaches every pick and the browser auto-scrolls the
 * focused card into view natively (no extra `tabIndex`/ARIA needed).
 *
 * @since WEB-677 (Discovery v2 — DPL-27)
 * @since WEB-678 (Discovery v2 — DPL-27) — ≤720 snap rail
 * @since FRE-1418 — match ring + percent removed; the why-line leads
 */
import Image from 'next/image';
import * as React from 'react';
import { isCanonicalCategorySlug, type CanonicalCategorySlug } from '@loci/types';

import { cn } from '@/components/atoms/primitives/utils';
import { buildEventImageSources } from '@/lib/image-proxy/eventImageSources';
import type { DiscoverPickCardModel } from '@/lib/discover/pickSet';

/**
 * DES-63 category color tokens (`styles/tokens/color.css`), keyed by canonical slug —
 * `satisfies` makes a missing or retired slug (`tech`) a compile error.
 */
const CATEGORY_COLOR_VAR = {
  music: 'var(--cat-music)',
  sports: 'var(--cat-sports)',
  food: 'var(--cat-food)',
  'science-tech': 'var(--cat-science-tech)',
  art: 'var(--cat-art)',
  drinks: 'var(--cat-drinks)',
  'business-networking': 'var(--cat-business-networking)',
  'community-culture': 'var(--cat-community-culture)',
  wellness: 'var(--cat-wellness)',
  'hobbies-interests': 'var(--cat-hobbies-interests)',
} as const satisfies Record<CanonicalCategorySlug, string>;

/** Neutral fallback — mirrors `.dv-ev`'s default `--cat: var(--fg-2)` (DES-63). */
function resolveCategoryColorVar(slug: string | undefined): string {
  return slug !== undefined && isCanonicalCategorySlug(slug)
    ? CATEGORY_COLOR_VAR[slug]
    : 'var(--fg-2)';
}

export interface DiscoverPickSetProps {
  /** Eyebrow label, e.g. `Picked for you` or `Best of what's still ahead`. */
  readonly eyebrow: string;
  /** Right-aligned caption, e.g. `3 of 5 taste matches`. */
  readonly caption: string;
  /** 1–3 ranked picks. Renders exactly this many cards — never padded. */
  readonly picks: readonly DiscoverPickCardModel[];
  /** Additional CSS classes merged onto the outer section. */
  readonly className?: string;
}

function DiscoverPickCard({ pick }: { pick: DiscoverPickCardModel }): React.ReactElement {
  // WEB-817 — `next/image` builds its own `srcset`, so the win here is a
  // right-sized optimizer INPUT: a 56px slot asks for the `thumbnail` band
  // instead of handing over the full-size cover. No `sizes` goes to the
  // builder because a custom `srcset` would be ignored by `next/image`.
  const { src: displayImageUrl } = buildEventImageSources({
    imageUrl: pick.imageUrl,
    imageUrls: pick.imageUrls,
    preferredBand: 'thumbnail',
  });
  const hasImage = displayImageUrl.length > 0;
  // FRE-1418 — the name carries the reason, never a score.
  const accessibleName = pick.whyLine !== null ? `${pick.title}, ${pick.whyLine}` : pick.title;

  return (
    <a
      href={pick.href}
      data-testid="discover-pick-card"
      aria-label={accessibleName}
      className={cn(
        // Mobile-first base = the WEB-678 ≤720 snap-rail card: 108px floor
        // (WEB-789 wrap may grow), sized to 82% of the rail (never fully
        // filling it, so the next card always peeks), snapping flush at its
        // own start edge.
        'dv-pick-card group relative flex min-h-[108px] flex-col overflow-hidden rounded-xl no-underline',
        'border border-loci-border bg-loci-surface',
        'transition-colors motion-safe:duration-150 hover:border-ds-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-loci-background',
        'shrink-0 grow-0 basis-[82%] min-w-[248px] max-w-[320px] [scroll-snap-align:start]',
        // WEB-677 — desktop/tablet grid card: one grid cell (no basis clamp,
        // no scroll-snap — the ancestor is a non-scrolling grid there).
        // WEB-1121 — it keeps the 108px floor. WEB-677's 137px came from the
        // Discovery v2 handoff (DPL-27 "Card (137px)"); the frozen v3.1 mock
        // (docs/design-system/1.9.0/templates/discover.html) supersedes it
        // with a 108.5px card, and 137 only padded ~92px of content.
        'min-[721px]:min-w-0 min-[721px]:max-w-none min-[721px]:basis-auto min-[721px]:grow',
      )}
    >
      <div className="flex min-h-0 flex-1 items-start gap-2 p-2.5 pb-1">
        <div
          data-testid="discover-pick-card-media"
          data-poster={hasImage ? 'present' : 'pending'}
          className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md border-l-3 min-[721px]:h-14 min-[721px]:w-14"
          style={{ borderLeftColor: resolveCategoryColorVar(pick.categorySlug) }}
        >
          {hasImage ? (
            // alt="" — the anchor's `aria-label` already names the event; the
            // poster is decorative. `sizes` matches the fixed 56px slot.
            <Image
              src={displayImageUrl}
              alt=""
              fill
              loading="lazy"
              sizes="56px"
              className="object-cover"
            />
          ) : (
            <div className="grid h-full w-full place-items-center bg-loci-surface-alt">
              {/* Hatched neutral placeholder — never a gradient fill, never a
                  broken image (matches the established `UpNextEventCard`
                  "poster pending" pattern). */}
              <span
                aria-hidden="true"
                className="absolute inset-0 bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(127,127,127,0.08)_6px_12px)]"
              />
              <span
                data-testid="discover-pick-card-poster-pending"
                className="relative px-0.5 text-center text-micro leading-[1.1] text-fg-3 font-mono max-[720px]:sr-only"
              >
                Poster
                <br />
                pending
              </span>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3
            data-testid="discover-pick-card-title"
            className="m-0 line-clamp-1 text-small font-semibold leading-[1.15] text-loci-text min-[721px]:line-clamp-2 font-display"
          >
            {pick.title}
          </h3>
          {pick.whyLine !== null && (
            <p
              data-testid="discover-pick-card-why"
              // WEB-755 — `text-[var(--accent-text)]` was read back via
              // getComputedStyle in a real browser: dark `rgb(79, 196, 196)`,
              // light `rgb(10, 99, 99)`. WEB-753's silent-no-CSS failure
              // does not reproduce on this class.
              className="m-0 line-clamp-1 text-micro leading-[1.25] text-accent-text min-[721px]:line-clamp-2"
            >
              {pick.whyLine}
            </p>
          )}
        </div>
      </div>

      <div
        data-testid="discover-pick-card-meta"
        className="dv-pick__m border-t border-loci-border px-2.5 py-1.25 text-micro leading-none tracking-[0.06em] text-loci-text-muted font-mono"
      >
        {pick.metaSegments.map((segment, index) => (
          <React.Fragment key={segment}>
            {index > 0 && <span aria-hidden="true">·</span>}
            <span className={index === 0 ? 'dv-pick__m-sig' : undefined}>{segment}</span>
          </React.Fragment>
        ))}
      </div>
    </a>
  );
}

export function DiscoverPickSet({
  eyebrow,
  caption,
  picks,
  className,
}: DiscoverPickSetProps): React.ReactElement | null {
  if (picks.length === 0) return null;

  return (
    <section
      data-testid="discover-pick-set"
      aria-label={eyebrow}
      className={cn('dv-pickset', className)}
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span
          data-testid="discover-pick-set-eyebrow"
          // WEB-755 — same live computed values as the why-line; the
          // `bg-current` glow child inherits this colour, not a fallback.
          className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-micro tracking-[0.16em] text-accent-text font-mono"
        >
          <span
            aria-hidden="true"
            data-testid="discover-pick-set-eyebrow-glow"
            className="dv-pickset__glow size-1.5 rounded-full bg-current shadow-[0_0_8px_currentColor]"
          />
          {eyebrow}
        </span>
        <span
          data-testid="discover-pick-set-caption"
          className="dv-pickset__caption min-w-0 flex-1 text-right text-micro tracking-[0.04em] text-loci-text-muted font-mono"
        >
          {caption}
        </span>
      </div>

      <div
        data-testid="discover-pick-set-grid"
        className={cn(
          // Mobile-first base = the WEB-678 ≤720 horizontal snap rail.
          // `overflow-x-auto` (never `hidden`) keeps the region trackpad/
          // touch-scrollable and lets Tab auto-scroll a focused card into
          // view; the scrollbar is hidden purely cosmetically. No negative
          // margin anywhere — the rail's own left edge is already the
          // container's padding edge, so it starts flush at the gutter and
          // bleeds right only (content simply overflows the visible width;
          // nothing pulls the start edge left).
          'flex flex-nowrap gap-3 overflow-x-auto overscroll-x-contain',
          '[scroll-snap-type:x_mandatory] [scroll-padding-left:0px]',
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          // WEB-677 — desktop/tablet: a non-scrolling auto-fit grid.
          'min-[721px]:grid min-[721px]:grid-cols-[repeat(auto-fit,minmax(272px,1fr))]',
          'min-[721px]:overflow-visible',
        )}
      >
        {picks.map((pick) => (
          <DiscoverPickCard key={pick.eventId} pick={pick} />
        ))}
      </div>
    </section>
  );
}
